import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Workflow } from "lucide-react";
import { useProcesses } from "@/api/queries/processes";
import { useUserContext, darfVorgang } from "@/auth/operations";
import { useUsers } from "@/api/queries/users";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTableShell } from "@/components/ResourceShell";
import { EntityLink } from "@/components/EntityLink";
import { ScaffoldProcessDialog } from "@/components/process-actions/ScaffoldProcessDialog";
import type { ProcessDefinitionView } from "@/types";

// #282 (UC-6): Betreiber-Sicht — bewusst schlank. Die Autoren-Fläche (Steps bauen etc.)
// gehört ins Prozess-Frontend (ElySphere §14.2), nicht in die Plattform-Admin-UI.

export function ProcessStatusBadge({ status }: { status: ProcessDefinitionView["status"] }) {
  switch (status) {
    case "active":
      return <Badge variant="active">aktiv</Badge>;
    case "paused":
      return <Badge variant="busy">pausiert</Badge>;
    default:
      return <Badge variant="default">Entwurf</Badge>;
  }
}

export function ProcessesList() {
  const { data, isLoading, isError, error, refetch } = useProcesses();
  const users = useUsers();
  const ctx = useUserContext();
  // M5 (Review): die Werkbank braucht git-content-Rechte — einem process-owner ohne sie
  // keinen Link anbieten, der in einem 404 endet (Server entscheidet, UI blendet nur aus).
  const darfRepositories = darfVorgang(ctx.data, "repositories.view");
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const rows = data ?? [];
  const userMap = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u] as const)), [users.data]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">
          {rows.length} Prozesse · Definitionen sind governed (Entwurf → Freigabe → Version)
        </p>
        <Button onClick={() => setCreateOpen(true)} data-testid="action-create-process">
          <Plus className="h-4 w-4" />
          Prozess anlegen
        </Button>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && rows.length === 0}
        emptyMessage="Noch keine Prozesse"
        emptyHint="Ein Prozess beschreibt einen governed Ablauf (process.json). Aktivieren markiert eine Version als gültig — ausgeführt wird erst mit der Lauf-Engine."
        onRetry={() => refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              {/* #303 (Review L2): „Verantwortlich" ist das Wort dieser Oberfläche (#292) —
                  „Owner" war ein Rest aus der Bau-Zeit und hiess an der Detail-Seite schon
                  anders. Zwei Wörter für dieselbe Person lesen sich wie zwei Rollen. */}
              <TableHead>Verantwortlich</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Aktive Version</TableHead>
              <TableHead>Repository</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-clickable="true" onClick={() => navigate(`/processes/${p.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                      <Workflow className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-semibold text-text">{p.name}</div>
                      <div className="font-mono text-[11.5px] text-text3">{p.id}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <EntityLink
                    to={`/users/${p.ownerId}`}
                    id={p.ownerId}
                    label={userMap.get(p.ownerId)?.email}
                    isResolving={users.isLoading || users.isError}
                  />
                </TableCell>
                <TableCell>
                  <ProcessStatusBadge status={p.status} />
                </TableCell>
                <TableCell>
                  <span className="font-mono text-[12px] text-text2">{p.activation?.version ?? "—"}</span>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {darfRepositories ? (
                    <Link
                      to={`/repositories?repo=${encodeURIComponent(p.managedRepoId ?? `prc-${p.id}`)}`}
                      className="font-mono text-[12px] text-accent hover:underline"
                    >
                      {p.repoName}
                    </Link>
                  ) : (
                    <span className="font-mono text-[12px] text-text2">{p.repoName}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      <ScaffoldProcessDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
